import { FC } from 'react'
import { useDrop } from 'react-dnd'
import css from 'classnames'
import { useTranslation } from 'react-i18next'
import { Props } from './types'

import { CategoryItem } from '../dnd/category'
import { NestedCategories } from '~/shared/api'
import {
  findById,
  insertElementWithShift,
  recursiveMove,
  recursiveRemove,
} from '../../lib/helpers'
import { isEqual, useToast } from '~/shared/lib'
import { SVGDriveFileMove } from '~/shared/ui'

export const CategoriesList: FC<Props> = ({ data, setList, updated }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const update = (id: string, targetEl: NestedCategories) => {
    const elem = findById(data, id)

    if (elem) {
      if (elem.id !== targetEl.id) {
        setList((prev) => {
          const newData = recursiveMove(prev, elem.id, targetEl.id)
          if (!isEqual(prev, newData)) {
            updated()
            return newData
          }
          toast.open(t('categoryAlreadyChild'), 'danger')
          return prev
        })
      } else if (elem.id === targetEl.id) {
        toast.open(t('cannotMoveCategoryIntoItself'), 'danger')
      }
    }
  }

  const [collectTop, dropTopDrag] = useDrop({
    accept: ['sub-category', 'sub-category-2', 'sub-category-3'],

    drop: (item: NestedCategories) => {
      const filtered = recursiveRemove(data, item.id)
      const newData = insertElementWithShift(filtered, 0, item)

      setList(newData)

      updated()
    },

    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  return (
    <div className="flex flex-col gap-3 w-full overflow-y-auto">
      {data.length ? (
        <div
          ref={dropTopDrag}
          className={css(
            'w-full h-[60px] font-light border-gray-300 dashed px-6 rounded-lg flex items-center gap-3 justify-center',
            {
              'bg-gray-300 opacity-70': collectTop.isOver,
            },
          )}
        >
          <SVGDriveFileMove />
          <span className="text-gray-600 text-[14px]">
            {t('raiseCategoryToTopLevel')}
          </span>
        </div>
      ) : null}

      {data.map((item, i) =>
        item.name ? (
          <CategoryItem
            key={item.id}
            data={item}
            index={i}
            categoriesList={data}
            updateList={update}
            setList={setList}
            updated={updated}
          />
        ) : null,
      )}
    </div>
  )
}
